import { Navigate, Outlet } from 'react-router-dom'
import { isIndependentUser } from '@shared/rbac/member-roles'
import { isSessionsNavVisibleFor } from '../config/nav-config'
import { useSessionsAccessInputs } from '../hooks/use-sessions-nav-visible'
import { followsAnySessions, retrySessionFollowing } from '../stores/session-following-store'
import { RouteFallback } from './route-fallback'

/**
 * Route guard for the Sessions surface — providers, members, and Independents
 * who follow at least one member's sessions (see `isSessionsNavVisibleFor`;
 * everyone else is redirected). Renders inside `ProtectedRoute`, so the user
 * is already signed in.
 *
 * Decides synchronously from the auth user's `roleType` when present;
 * otherwise waits for the `me` profile before deciding, so an eligible user
 * whose auth seed is missing role data is not bounced by a premature redirect.
 * Independents wait for the server follow summary. Fails CLOSED: no role data
 * denies access; a summary error shows a retry and never renders the page.
 */
export function RequireSessionsAccess() {
  const { profile, settled, following } = useSessionsAccessInputs()

  if (profile?.roleType == null) {
    // No role yet: wait for the `me` profile; still none (or it failed) denies.
    if (!settled) return <RouteFallback />
    return <Navigate to="/messages" replace />
  }
  if (isIndependentUser(profile) && (following.status === 'idle' || following.status === 'loading')) {
    return <RouteFallback />
  }
  if (isIndependentUser(profile) && following.status === 'error') {
    // Still closed (no Outlet) — but a deep link from a follower push gets a
    // retry instead of a silent bounce to Messages.
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div
          role="alert"
          className="flex max-w-sm flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-800"
        >
          <span>We couldn&apos;t check which sessions you follow.</span>
          <button
            type="button"
            onClick={() => void retrySessionFollowing()}
            aria-label="Retry loading the sessions you follow"
            className="a11y-touch-target rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-800 hover:bg-red-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }
  const allowed = isSessionsNavVisibleFor(profile, { followsSessions: followsAnySessions(following) })
  if (!allowed) return <Navigate to="/messages" replace />
  return <Outlet />
}
